/**
 * Auth API Service
 * Authentication related API calls
 */

import { api } from '@/shared/api/apiClient';
import { API_ROUTES } from '@/config/apiRoutes';

// ===========================================
// Types
// ===========================================
export interface CheckEmailResponse {
    available: boolean;
}

export interface VerifyLicenseRequest {
    state: string;
    license_number: string;
}

/** Flat, as the backend returns it (backend/app/schemas/user.py). */
export interface VerifyLicenseResponse {
    verified: boolean;
    message?: string;
    /** ok | not_found | unavailable | manual */
    status?: string;
    state?: string;
    agent_name?: string | null;
    office_name?: string | null;
    license_type?: string | null;
    agent_refnum?: string | null;
    office_license_refnum?: string | null;
    license_status?: string | null;
    expires_on?: string | null;
    /** The state board's own lookup page, for states without an automated source. */
    lookup_url?: string | null;
}

// ===========================================
// Auth Service
// ===========================================
export const authApi = {
    /**
     * Check if email is available for registration
     */
    checkEmailAvailability: async (email: string): Promise<CheckEmailResponse> => {
        const result = await api.post<{ exists: boolean }>(API_ROUTES.auth.checkEmail, { email });
        return { available: !result.exists };
    },

    /**
     * Verify realtor license number
     */
    verifyLicense: async (data: VerifyLicenseRequest): Promise<VerifyLicenseResponse> => {
        return api.post<VerifyLicenseResponse>(API_ROUTES.auth.verifyLicense, data);
    },

    /**
     * Verify email with token
     */
    verifyEmail: async (token: string): Promise<{ success: boolean; message: string }> => {
        return api.get(API_ROUTES.auth.verifyEmail, { token });
    },

    /**
     * Resend verification email
     */
    resendVerification: async (email: string): Promise<{ success: boolean; message: string }> => {
        return api.post(`${API_ROUTES.auth.resendVerification}?email=${encodeURIComponent(email)}`);
    },

    /**
     * Resend verification email (alias)
     */
    resendVerificationEmail: async (email: string): Promise<{ success: boolean; message: string }> => {
        return api.post(`${API_ROUTES.auth.resendVerification}?email=${encodeURIComponent(email)}`);
    },

    /**
     * Register as buyer
     */
    registerBuyer: async (data: Record<string, unknown>): Promise<unknown> => {
        return api.post(API_ROUTES.auth.registerBuyer, data);
    },

    /**
     * Register as seller
     */
    registerSeller: async (data: Record<string, unknown>): Promise<unknown> => {
        return api.post(API_ROUTES.auth.registerSeller, data);
    },

    /**
     * Register as realtor/broker
     */
    registerRealtor: async (data: Record<string, unknown>): Promise<unknown> => {
        return api.post(API_ROUTES.auth.registerRealtor, data);
    },

    /**
     * Register as other professional
     */
    registerProfessional: async (data: Record<string, unknown>): Promise<unknown> => {
        return api.post(API_ROUTES.auth.registerProfessional, data);
    },
};

export default authApi;
